<template>
  <!-- Only shown where the app can actually be installed: on an already
       installed instance, or a browser that cannot install, the button would
       be a dead end. Safari can, but only from its own menu, so there the
       button opens a guide to that menu instead of the browser's dialog (drawn
       once, by the layout: the copy in the user menu is unmounted the moment
       the click closes the menu, and would take the guide with it). -->
  <button
    v-if="!isInstalled && (installAvailable || manualInstall)"
    type="button"
    class="install"
    :title="t('pwa.installHint')"
    @click="install"
  >
    <AppIcon name="download" :size="15" />
    <span v-if="!compact" class="install__label">{{ t('pwa.install') }}</span>
  </button>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useToast } from '@general/ui-kit'

import AppIcon from './AppIcon.vue'
import { installAvailable, installGuideOpen, isInstalled, manualInstall, promptInstall } from '../app/pwa'

// Icon only. Used by the top bar, where the label was the first thing to go
// when the window narrowed; in the user menu the button gets to say what it is.
defineProps({ compact: Boolean })

const { t } = useI18n()
const toast = useToast()

async function install() {
  // The browser's own dialog wins whenever there is one.
  if (installAvailable.value) {
    if (await promptInstall()) toast.success(t('pwa.installed'))
    return
  }
  installGuideOpen.value = true
}
</script>

<style scoped>
.install {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  border: 1px solid var(--color-primary);
  border-radius: var(--radius);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--text-sm);
  font-weight: 550;
}
.install:hover { background: var(--color-primary); color: var(--color-text-on-primary); }

@media (pointer: coarse) { .install { height: 42px; } }
</style>
